// The Vent tab: the ComfoAir unit's airflow (intake / extract into the heat exchanger, supply / exhaust out,
// coloured by air temperature, faster with more fan), the level with the energy controller's 12 h plan and the
// rooms' CO2, the unit's status (filter, bypass, preheat, hours by level), and the History card (air
// temperatures, level, CO2).
// Data: GET /ventilation/state (vent.js posts every 60 s), /energy/plan (plan, uHoo), /data/vent_* exports
// (scripts/export.sh), uhoo_<range>.json for CO2 beyond 24 h (up to 1 month). Everything optional is hidden when missing.
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Alert } from 'react-bootstrap';

import { EnergyPlanSnapshot, VentLevel, VentilationState } from '../../common';
import { LayoutSize, layoutFor, useInterval, useViewportWidth } from '../util';
import { VENT_NAMES, airRooms, ventIndex, ventLevelNow } from '../util/air';
import { capitalize, formatAge, hhmm, num1 } from '../util/format';
import { HistoryRange, degrees, isBucketed, rangeInfo } from '../util/history';
import { HomeData, useHomeData } from '../util/home_data';
import { useHeaderStatus } from '../util/header_status';
import { Point, RrdFile, hasValues, latest, rrdColumn, rrdStepMs, valueAt } from '../util/rrd';
import { formatRelative, localMinuteOfDay } from '../util/tallinn_time';
import { HistoryCard, HistoryPane, HistorySeries, fittedAxis } from './history_card';
import { AirRoomRow } from './ui/air';
import { Badge, Card, DashMark, LineMark, Stat, pageStyle } from './ui/card';
import { LEVEL_LABELS, ppmLabel } from './ui/chart';
import { ToggleChip } from './ui/controls';
import { FlowBox, FlowCorner, FlowHub, FlowLink, clampDuration, flowNodeStyle } from './ui/flow';
import { HeaderStatus } from './ui/header_status';
import {
  ALERT,
  CARD_BG,
  CO2_COLORS,
  CO2_GOOD_LINE,
  CO2_HIGH_LINE,
  DOT,
  FAINT,
  HAIRLINE,
  INK,
  INK_SOFT,
  LEVEL_TINTS,
  MUTED,
  OK_TINT,
  PAGE_BG,
  SECONDARY,
  VENT_COLOR,
  VENT_TINT,
  WARN,
  ZERO_LINE,
} from './ui/tokens';

const H = 3_600_000;
const SLOT_MS = 15 * 60_000;
const UNIT_STALE_AFTER_MS = 3 * 60_000;
const PLAN_STALE_AFTER_MS = 30 * 60_000;
const FILTER_LIMIT_H = 4380; // replace the filter every 6 months of running
const COLD = '#2a78d6';
const WARM = '#eb6834';
const airColor = (c: number) => (c < 10 ? COLD : WARM);

const AIR = [
  { id: 'intake', name: 'Intake', legend: 'Välisõhk', color: '#2a78d6' },
  { id: 'supply', name: 'Supply', legend: 'Sissepuhe', color: '#eb6834' },
  { id: 'extract', name: 'Extract', legend: 'Toast tulev', color: '#c2410c' },
  { id: 'exhaust', name: 'Exhaust', legend: 'Väljapuhe', color: '#7aa7e0' },
] as const;
type AirId = (typeof AIR)[number]['id'];

// Heat recovered from the extract air: (supply − intake) / (extract − intake). Noise when the two ends are
// less than 5 °C apart.
function recovery(intake: number, supply: number, extract: number): number | undefined {
  return extract - intake < 5 ? undefined : Math.max(0, Math.min(100, ((supply - intake) / (extract - intake)) * 100));
}

const heaterIcon = (color: string, size: number) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M7 3c-1.5 2 1.5 4 0 6M12 3c-1.5 2 1.5 4 0 6M17 3c-1.5 2 1.5 4 0 6" />
    <rect x="3" y="13" width="18" height="8" rx="2" />
    <path d="M8 13v8M12 13v8M16 13v8" />
  </svg>
);

// ---- Unit card ----------------------------------------------------------------------------------------------

function AirNode(props: { corner: FlowCorner; temp: number; label: string }) {
  return (
    <div
      role="img"
      aria-label={`${props.label} ${num1(props.temp)} °C`}
      style={flowNodeStyle(props.corner, { background: CARD_BG, border: `2px solid ${airColor(props.temp)}` })}
    >
      <span className="num" style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.2 }}>
        {num1(props.temp)}°
      </span>
      <span style={{ fontSize: 10, color: SECONDARY }}>{props.label}</span>
    </div>
  );
}

const sideLabel = (left: number, text: string) => (
  <span
    style={{
      position: 'absolute',
      left,
      top: 104,
      width: 70,
      textAlign: 'center',
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: '0.06em',
      color: FAINT,
    }}
  >
    {text}
  </span>
);

function UnitCard(props: {
  vent: VentilationState;
  level: VentLevel;
  outdoor: number | undefined;
  now: number;
  size: LayoutSize;
  style?: React.CSSProperties;
}) {
  const { vent, now, size } = props;
  const t = vent.temperatures;
  const f = vent.airFlows;
  const stale = vent.receivedAt !== undefined && now - vent.receivedAt > UNIT_STALE_AFTER_MS;
  const running = vent.supplyFanRunning;
  const rec = recovery(t.outsideAir, t.supplyAir, t.outgoingAir);
  const sub = vent.bypassPercentage > 0 ? 'bypass open' : rec === undefined ? '—' : `${Math.round(rec)}% recovered`;
  const dur = (pct: number) => clampDuration(3.6 - (pct / 100) * 2.4);
  const link = (corner: FlowCorner, temp: number, pct: number, inflow: boolean) => (
    <FlowLink corner={corner} active={running} inflow={inflow} color={airColor(temp)} durationS={dur(pct)} />
  );
  const statSize = size === 'phone' ? 15 : 16;
  return (
    <Card
      label="Unit right now"
      raised
      style={{
        padding: size === 'phone' ? '12px 14px' : '14px 18px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: 10,
        ...props.style,
      }}
    >
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <FlowBox
          divider
          stale={stale}
          note={stale ? { text: `stale · ${formatRelative(vent.receivedAt!, now)}`, alert: true } : undefined}
          links={
            <>
              {link('topLeft', t.outsideAir, f.supplyAirPercentage, true)}
              {link('topRight', t.outgoingAir, f.outgoingAirPercentage, true)}
              {link('bottomRight', t.supplyAir, f.supplyAirPercentage, false)}
              {link('bottomLeft', t.exhaustAir, f.outgoingAirPercentage, false)}
            </>
          }
        >
          {sideLabel(10, 'OUTSIDE')}
          {sideLabel(250, 'HOUSE')}
          <AirNode corner="topLeft" temp={t.outsideAir} label="intake" />
          <AirNode corner="topRight" temp={t.outgoingAir} label="from rooms" />
          <AirNode corner="bottomLeft" temp={t.exhaustAir} label="exhaust" />
          <AirNode corner="bottomRight" temp={t.supplyAir} label="to rooms" />
          {vent.preheat && (
            <span
              role="status"
              style={{
                position: 'absolute',
                left: 45,
                top: 65,
                zIndex: 1,
                transform: 'translateX(-50%)',
                whiteSpace: 'nowrap',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '2px 8px 2px 6px',
                borderRadius: 999,
                background: ALERT.fill,
                color: '#ffffff',
                fontSize: 11,
                fontWeight: 600,
                boxShadow: `0 0 0 2px ${CARD_BG}`,
              }}
            >
              {heaterIcon('#ffffff', 13)}
              Preheat
            </span>
          )}
          <FlowHub tint={VENT_TINT} title={VENT_NAMES[props.level]} sub={sub} />
        </FlowBox>
      </div>
      <div
        className="num"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 8,
          paddingTop: 10,
          borderTop: `1px solid ${HAIRLINE}`,
        }}
      >
        <Stat label="Supply fan" size={statSize} sub={`${f.rotationsSupply} rpm`}>
          {f.supplyAirPercentage}%
        </Stat>
        <Stat label="Exhaust fan" size={statSize} sub={`${f.rotationsOutgoing} rpm`}>
          {f.outgoingAirPercentage}%
        </Stat>
        <Stat
          label="Outdoor"
          size={statSize}
          right
          sub={vent.preheat ? 'preheat on' : 'preheat off'}
          subStyle={vent.preheat ? { color: ALERT.strong, fontWeight: 600 } : undefined}
        >
          {props.outdoor !== undefined ? `${num1(props.outdoor)}°` : '—'}
        </Stat>
      </div>
    </Card>
  );
}

// ---- Level card ---------------------------------------------------------------------------------------------

interface PlanRun {
  level: VentLevel;
  start: number;
  end: number;
}

// Runs of equal planned level over [now, now + 12 h].
function planRuns(plan: EnergyPlanSnapshot | undefined, now: number): PlanRun[] {
  const runs: PlanRun[] = [];
  const end = now + 12 * H;
  for (const s of plan?.slots ?? []) {
    const a = Math.max(s.start, now);
    const b = Math.min(s.start + SLOT_MS, end);
    if (!s.ventLevel || b <= a) {
      continue;
    }
    const last = runs[runs.length - 1];
    if (last && last.level === s.ventLevel && last.end === a) {
      last.end = b;
    } else {
      runs.push({ level: s.ventLevel, start: a, end: b });
    }
  }
  return runs;
}

function PlanStrip(props: { runs: PlanRun[]; now: number }) {
  const { runs, now } = props;
  const span = 12 * H;
  const next = runs[1];
  const ticks: { left: number; label: string }[] = [];
  for (let t = Math.ceil((now + H) / H) * H; t < now + 11.5 * H; t += H) {
    if (localMinuteOfDay(t) % 180 === 0) {
      ticks.push({ left: ((t - now) / span) * 100, label: hhmm(t).slice(0, 2) });
    }
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div className="num" style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: SECONDARY }}>
        <span>Plan · next 12 h</span>
        <span>
          next: <b style={{ color: INK }}>{next ? `${VENT_NAMES[next.level]} at ${hhmm(next.start)}` : 'no change'}</b>
        </span>
      </div>
      <div style={{ display: 'flex', height: 22, borderRadius: 6, overflow: 'hidden', gap: 1, background: CARD_BG }}>
        {runs.map((r) => (
          <span
            key={r.start}
            title={`${hhmm(r.start)}–${hhmm(r.end)} ${VENT_NAMES[r.level]}`}
            style={{
              flex: 'none',
              width: `${((r.end - r.start) / span) * 100}%`,
              background: LEVEL_TINTS[r.level],
              color: r.level === 'high' ? '#ffffff' : INK,
              fontSize: 10,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              paddingLeft: 5,
              boxSizing: 'border-box',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            {r.end - r.start >= 1.25 * H ? VENT_NAMES[r.level] : ''}
          </span>
        ))}
      </div>
      <div className="num" style={{ position: 'relative', height: 13, fontSize: 10, color: MUTED }}>
        <span style={{ position: 'absolute', left: 0 }}>now</span>
        {ticks.map((k) => (
          <span key={k.left} style={{ position: 'absolute', left: `${k.left}%`, transform: 'translateX(-50%)' }}>
            {k.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function modeBadge(plan: EnergyPlanSnapshot | undefined, vent: VentilationState | undefined) {
  if ((plan?.ventManualOverrideSince ?? vent?.control?.manualOverrideSince) !== undefined) {
    return (
      <Badge bg={WARN.bg} color={WARN.fg} dot={DOT.warning}>
        Manual override
      </Badge>
    );
  }
  switch (plan?.ventilationMode) {
    case 'live':
      return (
        <Badge bg={OK_TINT} dot={DOT.good}>
          Auto · live
        </Badge>
      );
    case 'shadow':
      return <Badge bg={HAIRLINE}>Auto · shadow</Badge>;
    case 'off':
      return <Badge bg={HAIRLINE}>Manual</Badge>;
    default:
      return null;
  }
}

function LevelCard(props: {
  plan: EnergyPlanSnapshot | undefined;
  vent: VentilationState | undefined;
  level: VentLevel | undefined;
  now: number;
}) {
  const { plan, vent, level, now } = props;
  const runs = planRuns(plan, now);
  const rooms = airRooms(plan, now);
  const pct = vent?.levelPercents;
  return (
    <Card label="Level and air" style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 14, fontWeight: 600 }}>Level</span>
        {modeBadge(plan, vent)}
      </div>
      <div
        role="list"
        aria-label="Fan % per level"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 6 }}
      >
        {(['low', 'middle', 'high'] as const).map((l) => {
          const on = l === level;
          const p = pct?.[l];
          return (
            <div
              key={l}
              role="listitem"
              aria-current={on}
              className="num"
              style={{
                borderRadius: 10,
                padding: '7px 10px',
                background: on ? VENT_COLOR : PAGE_BG,
                color: on ? '#ffffff' : INK_SOFT,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                gap: 4,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {VENT_NAMES[l]}
              {p && (
                <span style={{ fontSize: 11, fontWeight: 400, color: on ? 'rgba(255,255,255,0.8)' : MUTED }}>
                  {p.supply === p.exhaust ? `${p.supply}%` : `${p.supply}/${p.exhaust}%`}
                </span>
              )}
            </div>
          );
        })}
      </div>
      {runs.length > 0 && <PlanStrip runs={runs} now={now} />}
      {rooms.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            paddingTop: 8,
            borderTop: `1px solid ${HAIRLINE}`,
          }}
        >
          {rooms.map((r) => (
            <AirRoomRow key={r.name} room={r} co2Label />
          ))}
        </div>
      )}
    </Card>
  );
}

// ---- Unit status card ---------------------------------------------------------------------------------------

type Hours30 = Record<'hAway' | 'hLow' | 'hMiddle' | 'hHigh' | 'hFrost' | 'hPreheat' | 'hBypass', number>;

// Counter growth over the file: last non-null row − first non-null row, if they are at least 30 days apart.
function hoursOver30Days(file: RrdFile | undefined): Hours30 | undefined {
  const keys: (keyof Hours30)[] = ['hAway', 'hLow', 'hMiddle', 'hHigh', 'hFrost', 'hPreheat', 'hBypass'];
  const out = {} as Hours30;
  for (const k of keys) {
    const pts = rrdColumn(file, k)?.filter((p) => p[1] !== null) as [number, number][] | undefined;
    if (!pts || pts.length < 2 || pts[pts.length - 1][0] - pts[0][0] < 30 * 24 * H - 60_000) {
      return undefined;
    }
    out[k] = Math.max(0, Math.round(pts[pts.length - 1][1] - pts[0][1]));
  }
  return out;
}

function StatusCard(props: { vent: VentilationState; hours30: Hours30 | undefined; style?: React.CSSProperties }) {
  const { vent, hours30: h } = props;
  const filter = vent.filterHours;
  const filterColor = filter > FILTER_LIMIT_H ? ALERT.fill : filter > FILTER_LIMIT_H * 0.85 ? DOT.warning : VENT_COLOR;
  const daysLeft = Math.round((FILTER_LIMIT_H - filter) / 24);
  const ph = vent.preheat;
  const levels = h
    ? (
        [
          ['Away', h.hAway, '#d6d5ce'],
          ['Low', h.hLow, LEVEL_TINTS.low],
          ['Middle', h.hMiddle, LEVEL_TINTS.middle],
          ['High', h.hHigh, LEVEL_TINTS.high],
        ] as [string, number, string][]
      ).filter((x) => x[1] > 0)
    : [];
  const total = levels.reduce((s, x) => s + x[1], 0);
  const op = vent.operatingHours;
  const lifetime = op ? op.away + op.low + op.middle + op.high : undefined;
  return (
    <Card
      label="Unit status"
      radius={16}
      style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10, ...props.style }}
    >
      {!vent.supplyFanRunning && (
        <div
          role="alert"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: ALERT.bg,
            color: ALERT.fg,
            borderRadius: 10,
            padding: '7px 10px',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: DOT.critical }} />
          Supply fan stopped
        </div>
      )}
      <div
        className="num"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px 14px' }}
      >
        <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, color: SECONDARY }}>
            <span>
              Filter <b style={{ color: INK }}>{filter} h</b> of {FILTER_LIMIT_H}
            </span>
            <span style={filter > FILTER_LIMIT_H ? { color: ALERT.strong, fontWeight: 600 } : undefined}>
              {filter > FILTER_LIMIT_H ? 'replace now' : `${daysLeft} days left`}
            </span>
          </div>
          <span style={{ height: 6, borderRadius: 3, background: HAIRLINE, position: 'relative', overflow: 'hidden' }}>
            <span
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: `${Math.min(100, (filter / FILTER_LIMIT_H) * 100).toFixed(1)}%`,
                background: filterColor,
                borderRadius: 3,
              }}
            />
          </span>
        </div>
        <Stat label="Bypass" size={14} sub={h ? `${h.hBypass} h open · 30 d` : undefined}>
          {vent.bypassPercentage > 0 ? `Open ${vent.bypassPercentage}%` : 'Closed'}
        </Stat>
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'flex-start',
            margin: '-6px -8px',
            padding: '6px 8px',
            borderRadius: 10,
            background: ph ? ALERT.bg : 'transparent',
            boxShadow: `inset 0 0 0 1px ${ph ? ALERT.ring : 'transparent'}`,
            minWidth: 0,
          }}
        >
          <span
            style={{
              flex: 'none',
              width: 28,
              height: 28,
              borderRadius: 8,
              background: ph ? ALERT.fill : PAGE_BG,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {heaterIcon(ph ? '#ffffff' : FAINT, 18)}
          </span>
          <span
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              fontSize: 11,
              color: ph ? '#a14545' : SECONDARY,
              minWidth: 0,
            }}
          >
            <span>Preheat</span>
            <b style={{ fontSize: 14, color: ph ? ALERT.fg : INK }}>{ph ? 'On' : 'Off'}</b>
            {h && (
              <span style={{ whiteSpace: 'nowrap' }}>
                {h.hPreheat} h · frost {h.hFrost} h · 30 d
              </span>
            )}
          </span>
        </div>
      </div>
      {total > 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            paddingTop: 8,
            borderTop: `1px solid ${HAIRLINE}`,
          }}
        >
          <div
            className="num"
            style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: SECONDARY }}
          >
            <span>Last 30 days by level</span>
            {lifetime !== undefined && <span>{lifetime} h lifetime</span>}
          </div>
          <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', gap: 1 }}>
            {levels.map(([name, hours, color]) => (
              <span
                key={name}
                style={{ flex: 'none', width: `${((hours / total) * 100).toFixed(1)}%`, background: color }}
              />
            ))}
          </div>
          <div
            className="num"
            style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', fontSize: 11, color: SECONDARY }}
          >
            {levels.map(([name, hours, color]) => (
              <span key={name} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: color }} />
                {name} {Math.round((hours / total) * 100)}% · {hours} h
              </span>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

// ---- History ------------------------------------------------------------------------------------------------

interface VentHistory {
  air: Partial<Record<AirId, Point[]>>;
  airStepMs: number;
  level?: Point[]; // 1 = low … 3 = high; rows at the end of their interval
  levelStepMs: number;
  co2: { name: string; points: Point[] }[];
  co2StepMs: number;
  notes: string[];
}

async function loadHistory(data: HomeData, range: HistoryRange) {
  const short = rangeInfo(range).hours <= 24;
  const [temps, level, uhoo] = await Promise.all([
    data.rrd(`vent_temperature${range}`),
    data.rrd(`vent_level${range}`),
    short ? Promise.resolve(undefined) : data.rrd(`uhoo_${range}`),
  ]);
  return { range, temps, level, uhoo, short };
}

function buildHistory(
  loaded: Awaited<ReturnType<typeof loadHistory>> | undefined,
  plan: EnergyPlanSnapshot | undefined,
) {
  if (!loaded) {
    return undefined;
  }
  const out: VentHistory = {
    air: {},
    airStepMs: rrdStepMs(loaded.temps) ?? 300_000,
    co2: [],
    co2StepMs: SLOT_MS,
    levelStepMs: SLOT_MS,
    notes: [],
  };
  for (const a of AIR) {
    const pts = rrdColumn(loaded.temps, a.legend);
    if (hasValues(pts)) {
      out.air[a.id] = pts;
    }
  }
  // Level: the export (1 = away … 4 = high), else the plan snapshot's measured slots (last 24 h).
  const level = rrdColumn(loaded.level, 'Tase', (v) => v - 1);
  if (hasValues(level)) {
    out.level = level;
    out.levelStepMs = rrdStepMs(loaded.level) ?? SLOT_MS;
  } else if (loaded.short && plan?.actual.some((a) => a.ventLevel)) {
    out.level = plan.actual.filter((a) => a.ventLevel).map((a): Point => [a.start + SLOT_MS, ventIndex(a.ventLevel)]);
  } else if (plan?.actual.some((a) => a.ventLevel)) {
    out.notes.push('Level history: last 24 h only');
  }
  // CO2: the snapshot's 15-minute means up to 24 h, the uHoo export beyond.
  const rooms = plan?.airQuality?.rooms ?? [];
  if (loaded.short) {
    out.co2 = rooms
      .filter((r) => r.slots.length > 0)
      .map((r) => ({ name: r.name, points: r.slots.map((s): Point => [s.start + SLOT_MS, s.co2]) }));
  } else {
    out.co2 = rooms
      .map((r) => ({ name: r.name, points: rrdColumn(loaded.uhoo, `${r.name} CO2`) }))
      .filter((r): r is { name: string; points: Point[] } => hasValues(r.points));
    out.co2StepMs = rrdStepMs(loaded.uhoo) ?? SLOT_MS;
    if (out.co2.length === 0 && rooms.some((r) => r.slots.length > 0)) {
      // The uHoo export stops at 1m; without it there is only the snapshot's last 24 h.
      out.notes.push(
        rangeInfo(loaded.range).hours > 720 ? 'CO₂ history: up to 1 month' : 'CO₂ history: last 24 h only',
      );
    }
  }
  return out;
}

function ventPanes(h: VentHistory, co2Max: number, thresholds: { good: number; high: number }): HistoryPane[] {
  const panes: HistoryPane[] = [];
  if (Object.keys(h.air).length > 0) {
    panes.push({
      key: 'air',
      title: 'Air °C',
      height: { phone: 150, tablet: 230, web: 250 },
      axis: {
        ...fittedAxis(0),
        labels: {
          formatter() {
            return degrees(this.value as number);
          },
        },
        plotLines: [{ value: 0, color: ZERO_LINE, dashStyle: 'Dash', width: 1, zIndex: 3 }],
      },
    });
  }
  if (h.level) {
    panes.push({
      key: 'level',
      height: { phone: 40, tablet: 50, web: 56 },
      axis: {
        min: 0.6,
        max: 3.4,
        tickPositions: [1, 2, 3],
        gridLineWidth: 0,
        labels: {
          formatter() {
            return LEVEL_LABELS[this.value as number] ?? '';
          },
        },
      },
    });
  }
  if (h.co2.length > 0) {
    panes.push({
      key: 'co2',
      title: 'CO₂ ppm',
      height: { phone: 64, tablet: 96, web: 100 },
      axis: {
        min: 400,
        max: co2Max,
        tickPositions: [400, thresholds.good, thresholds.high, co2Max],
        gridLineWidth: 0,
        labels: {
          formatter() {
            return ppmLabel(this.value as number, true);
          },
        },
        plotLines: [
          { value: thresholds.good, color: CO2_GOOD_LINE, dashStyle: 'ShortDash', width: 1, zIndex: 3 },
          { value: thresholds.high, color: CO2_HIGH_LINE, dashStyle: 'ShortDash', width: 1, zIndex: 3 },
        ],
      },
    });
  }
  return panes;
}

// ---- Tab ----------------------------------------------------------------------------------------------------

export const VentilationTab: React.FC = (): React.ReactElement => {
  const data = useHomeData();
  const size = layoutFor(useViewportWidth());
  const [now, setNow] = useState(data.now);
  const [vent, setVent] = useState<VentilationState | undefined>(undefined);
  const [ventLoaded, setVentLoaded] = useState(false);
  const [plan, setPlan] = useState<EnergyPlanSnapshot | undefined>(undefined);
  const [range, setRange] = useState<HistoryRange>('24h');
  const [loaded, setLoaded] = useState<Awaited<ReturnType<typeof loadHistory>> | undefined>(undefined);
  const [hours30, setHours30] = useState<Hours30 | undefined>(undefined);
  const [outdoorRrd, setOutdoorRrd] = useState<number | undefined>(undefined);
  const [hidden, setHidden] = useState<Set<AirId>>(new Set());

  useInterval(async () => {
    setNow(data.now());
    setVent(await data.vent());
    setVentLoaded(true);
  }, 15_000);
  useInterval(async () => {
    setNow(data.now());
    setPlan(await data.plan());
  }, 60_000);
  // Exports arrive every 5 minutes.
  useInterval(async () => {
    const [hours, outside] = await Promise.all([data.rrd('vent_hours30d'), data.rrd('outside_temperature3h')]);
    setHours30(hoursOver30Days(hours));
    setOutdoorRrd(latest(rrdColumn(outside, 'Temperatuur'))?.[1]);
    setLoaded(await loadHistory(data, range));
  }, 5 * 60_000);
  useEffect(() => {
    if (loaded && loaded.range !== range) {
      let cancelled = false;
      loadHistory(data, range).then((l) => {
        if (!cancelled) setLoaded(l);
      });
      return () => {
        cancelled = true;
      };
    }
    return undefined;
  }, [range]);

  const history = useMemo(() => buildHistory(loaded, plan), [loaded, plan]);
  const thresholds = plan?.airQuality?.thresholds ?? { good: 1000, high: 1200 };
  const co2Max = Math.max(
    thresholds.high + 200,
    Math.ceil(Math.max(0, ...(history?.co2 ?? []).flatMap((r) => r.points.map((p) => p[1] ?? 0))) / 200) * 200,
  );
  const panes = useMemo(
    () => (history ? ventPanes(history, co2Max, thresholds) : []),
    [history, co2Max, thresholds.good, thresholds.high],
  );
  const series = useMemo(() => {
    const out: HistorySeries[] = [];
    if (!history) {
      return out;
    }
    for (const a of AIR) {
      const pts = history.air[a.id];
      if (pts) {
        out.push({ id: `air-${a.id}`, pane: 'air', data: pts, color: a.color, visible: !hidden.has(a.id) });
      }
    }
    if (history.level) {
      out.push({
        id: 'level',
        pane: 'level',
        data: history.level,
        color: VENT_COLOR,
        type: 'area',
        step: 'right',
        fillOpacity: 0.14,
      });
    }
    history.co2.forEach((r, i) =>
      out.push({ id: `co2-${r.name}`, pane: 'co2', data: r.points, color: CO2_COLORS[i % CO2_COLORS.length] }),
    );
    return out;
  }, [history, hidden]);

  // Header: "● unit live · 40 s ago" from the last state the server received; the plan's age without one.
  const unitStale = vent?.receivedAt !== undefined && now - vent.receivedAt > UNIT_STALE_AFTER_MS;
  const unitAge = vent?.receivedAt !== undefined ? formatAge(vent.receivedAt, now) : undefined;
  const planAge = plan ? formatRelative(plan.createdAt, now) : undefined;
  const planStale = plan !== undefined && now - plan.createdAt > PLAN_STALE_AFTER_MS;
  const status = useMemo(() => {
    const planText = plan ? `plan ${planAge}` : undefined;
    if (unitAge !== undefined) {
      const lead = `unit ${unitStale ? 'stale' : 'live'} · ${unitAge}`;
      return (
        <HeaderStatus
          size={size}
          stale={unitStale}
          text={{
            phone: lead,
            tablet: [capitalize(lead), planText && capitalize(`${planText} · ${hhmm(plan!.createdAt)}`)],
            web: planText ? [lead, `${planText} (${hhmm(plan!.createdAt)})`] : [lead],
          }}
        />
      );
    }
    if (planText) {
      const lead = `${planStale ? 'stale · ' : ''}${planText}`;
      return (
        <HeaderStatus size={size} stale={planStale} text={{ phone: lead, tablet: [capitalize(lead)], web: [lead] }} />
      );
    }
    return null;
  }, [size, unitAge, unitStale, planAge, planStale]);
  useHeaderStatus(status);

  const page = pageStyle(size);
  if (!ventLoaded) {
    return (
      <div style={page}>
        <Alert variant="info" className="mb-0">
          Loading ventilation…
        </Alert>
      </div>
    );
  }

  const level = vent?.currentLevel ?? (plan ? ventLevelNow(plan, now) : undefined);
  const outdoor =
    outdoorRrd ?? [...(plan?.actual ?? [])].reverse().find((a) => a.outdoorTempC !== undefined)?.outdoorTempC;
  const phone = size === 'phone';
  const web = size === 'web';

  const unit = vent ? (
    <UnitCard vent={vent} level={level ?? vent.currentLevel} outdoor={outdoor} now={now} size={size} />
  ) : (
    <Alert variant="info" className="mb-0">
      No state from the ventilation unit yet (vent.js posts it every minute; add ?mock=vent to the URL for sample data).
    </Alert>
  );
  const levelCard = <LevelCard plan={plan} vent={vent} level={level} now={now} />;
  const statusCard = vent && <StatusCard vent={vent} hours30={hours30} style={phone ? undefined : { flex: 1 }} />;

  const info = rangeInfo(loaded?.range ?? range);
  const win: [number, number] = [now - info.hours * H, now];
  const bucketed = isBucketed(history?.levelStepMs);
  const at = (pts: Point[] | undefined, t: number | null, stepMs: number) =>
    t === null ? latest(pts)?.[1] : valueAt(pts, t, stepMs * 1.5);
  const chips = (t: number | null) => (
    <div
      role="group"
      aria-label="Air temperatures (tap to show or hide)"
      style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}
    >
      {AIR.filter((a) => history?.air[a.id]).map((a) => {
        const v = at(history!.air[a.id], t, history!.airStepMs);
        return (
          <ToggleChip
            key={a.id}
            name={a.name}
            color={a.color}
            on={!hidden.has(a.id)}
            size={size}
            value={v === undefined ? '—' : `${num1(v)}°`}
            onToggle={() => {
              const next = new Set(hidden);
              if (!next.delete(a.id)) next.add(a.id);
              setHidden(next);
            }}
          />
        );
      })}
    </div>
  );
  const readout = (t: number | null) => {
    if (!history) {
      return null;
    }
    const lv = at(history.level, t, history.levelStepMs);
    const lvName = lv === undefined ? undefined : (['away', 'low', 'middle', 'high'] as VentLevel[])[Math.round(lv)];
    const air = (id: AirId) => at(history.air[id], t, history.airStepMs);
    const [i, s, x] = [air('intake'), air('supply'), air('extract')];
    const rec = i !== undefined && s !== undefined && x !== undefined ? recovery(i, s, x) : undefined;
    const co2 = history.co2.map((r) => at(r.points, t, history.co2StepMs));
    const parts: [string, string][] = [];
    if (history.level) {
      parts.push(['Level', lvName ? (bucketed ? `avg ${lvName}` : VENT_NAMES[lvName]) : '—']);
    }
    if (i !== undefined || t === null) {
      parts.push(['Recovery', rec === undefined ? '—' : `${Math.round(rec)}%`]);
    }
    if (history.co2.length > 0) {
      parts.push([
        'CO₂',
        co2.some((v) => v !== undefined)
          ? `${co2.map((v) => (v === undefined ? '—' : Math.round(v))).join(' · ')} ppm`
          : '—',
      ]);
    }
    return (
      <div
        className="num"
        style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 12px', fontSize: 12, color: SECONDARY, padding: '0 2px' }}
      >
        {parts.map(([label, value]) => (
          <span key={label} style={{ whiteSpace: 'nowrap' }}>
            {label} <b style={{ color: INK }}>{value}</b>
          </span>
        ))}
      </div>
    );
  };
  const legendItems: [React.ReactNode, string][] = [];
  history?.co2.forEach((r, i) =>
    legendItems.push([<LineMark color={CO2_COLORS[i % CO2_COLORS.length]} height={2} />, r.name]),
  );
  const historyCard =
    history && panes.length > 0 ? (
      <HistoryCard
        size={size}
        now={now}
        range={range}
        onRange={setRange}
        win={win}
        panes={panes}
        series={series}
        minRangeMs={Math.max(15 * 60_000, 4 * history.airStepMs)}
        chips={(t) =>
          phone ? (
            <>
              {chips(t)}
              {readout(t)}
            </>
          ) : (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '8px 12px',
              }}
            >
              {chips(t)}
              {readout(t)}
            </div>
          )
        }
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px 12px',
            fontSize: 11,
            color: SECONDARY,
            padding: '2px 2px 0',
          }}
        >
          {legendItems.map(([mark, label]) => (
            <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {mark}
              {label}
            </span>
          ))}
          {history.co2.length > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <DashMark color={CO2_GOOD_LINE} />
              {thresholds.good} ppm
              <DashMark color={CO2_HIGH_LINE} style={{ marginLeft: 6 }} />
              {thresholds.high} ppm
            </span>
          )}
          {history.air.intake && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <DashMark color={ZERO_LINE} />0 °C
            </span>
          )}
          {history.notes.map((n) => (
            <span key={n} style={{ color: MUTED }}>
              {n}
            </span>
          ))}
        </div>
      </HistoryCard>
    ) : null;
  const footer = (
    <p className="num" style={{ margin: 0, fontSize: 11, color: MUTED }}>
      ComfoAir · intake is measured after the preheater, so it stays near −2 °C while preheat runs · level plan from the
      energy controller
    </p>
  );

  if (phone) {
    return (
      <div style={page}>
        {unit}
        {levelCard}
        {statusCard}
        {historyCard}
        {footer}
      </div>
    );
  }
  return (
    <div style={page}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: web ? 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)' : 'minmax(0, 1fr) minmax(0, 1fr)',
          gap: 16,
          alignItems: 'stretch',
        }}
      >
        {unit}
        {web ? (
          <>
            {levelCard}
            {statusCard}
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
            {levelCard}
            {statusCard}
          </div>
        )}
      </div>
      {historyCard}
      {footer}
    </div>
  );
};
